import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RestfulEndpoint } from '../restful-endpoint.service';
import {
  FlatFolder,
  FoldersListData,
  FoldersListResponse,
  FoldersParamsOptions,
} from '../../models/folders.model';
import { addLabelsParams, Folder } from '../../modules/projects/projects-api-models';

import { TranslocoService } from '@ngneat/transloco';
import { Projects2Service } from '../../modules/projects/projects2.service';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class FoldersService {
  //#region --Props section
  _foldersParams: FoldersParamsOptions = {} as FoldersParamsOptions;
  per_page: number;
  page: number = 1;
  flatFolderList: FlatFolder[];
  isLoading: boolean = false;
  folders: Folder[];
  totalFolders: number = 0;
  private foldersSubject: BehaviorSubject<Folder[]> = new BehaviorSubject<Folder[]>([]);
  readonly folders$: Observable<Folder[]> = this.foldersSubject.asObservable();
  private flatFoldersSubject: BehaviorSubject<FlatFolder[]> = new BehaviorSubject<FlatFolder[]>([]);
  currentPage: number;
  totalPages: number;
  searchText: string;
  folderList: Folder[] = [];
  folderMeta: {
    current_page: number;
    per_page: number;
    total_items: number;
    total_pages: number;
  };
  appliedFilter: any[];

  //#endregion --Props section

  constructor(
    private endPoint: RestfulEndpoint,
    private translateService: TranslocoService,
    private _toaster: ToastrService,
    private _project2: Projects2Service,
  ) {}

  async createNewFolder(newFolder: {
    parent_id: any;
    folder_name_en: any;
    folder_name: any;
  }): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.api_createNewFolder(newFolder).subscribe(
        async (foldersListRes) => {
          resolve(foldersListRes);
          this.page = 1;
          await this.fetchFoldersRefactored();
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  async deleteFolderRefactored(body: {
    flag: any;
    folder_id: any;
  }): Promise<{ success: string[] }> {
    return new Promise((resolve, reject) => {
      this.api_deleteFolder(body).subscribe(
        (foldersListRes) => {
          resolve(foldersListRes);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  async updateFolder(body: any): Promise<boolean> {
    const response = await this.api_updateFolder(body).toPromise();
    if (response.status) {
      this.page = 1;
      await this.fetchFoldersRefactored();
      return true;
    } else {
      return false;
    }
  }

  //the fetch method that handling the process of getting
  //the folder list
  async fetchFoldersRefactored(isSummaryReport?: boolean, label_name?: string): Promise<void> {
    this.isLoading = true;
    if (isSummaryReport) {
      this.per_page = 20;
      this.searchText = label_name;
    } else {
      this.per_page = 20;
      this.page = this.page ?? 1;
    }

    await this.getFoldersRefactored()
      .then((response) => {
        this.totalFolders = response.meta_data.total_items;
        this.currentPage = response.meta_data.current_page;
        this.totalPages = response.meta_data.total_pages;
        this.folders = this.sortFolders(response.data, isSummaryReport);
        this.foldersSubject.next(this.folders);
        this.flatFolderList = this.prepareFlatFolders(this.folders);
        this.flatFoldersSubject.next(this.flatFolderList);
        this.isLoading = false;
      })
      .catch((error) => {
        this._toaster.error(error.error?.message);
        this.isLoading = false;
      });
  }

  async moveProjectToFolder(
    project_ids: string[] | '*',
    folder_ids: addLabelsParams,
    exclude_ids?: string[],
  ): Promise<boolean> {
    try {
      const response = await this.api_moveToFolder({
        project_ids,
        label_ids: folder_ids.selectedLabels,
        excluded_label_ids: folder_ids.unselectedLabels,
        exclude_ids,
      }).toPromise();
      if (response.status == 'success') {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      // Handle the error here and display the toast message.
      if (
        error.status === 403 &&
        error.error?.error?.[0] === 'you do not have permission to update this project'
      ) {
        this._toaster.error(
          this.translateService.translate(
            'You do not have permission to update some of the projects.',
          ),
        );
      } else if (error.error?.errors?.length > 0) {
        error.error.errors.forEach((error) => {
          this._toaster.error(error);
        });
      } else {
        this._toaster.error(
          this.translateService.translate('Something went wrong, please try again.'),
        );
      }
      return false;
    }
  }

  private sortFolders(folders: FoldersListData[], isSummaryReport: boolean) {
    const sortedFolders = folders.sort((a, b) =>
      a.folder_name.toLowerCase() < b.folder_name.toLowerCase() ? -1 : 1,
    );
    if (this.page === 1 && !isSummaryReport) {
      return [
        {
          folder_name: this.translateService.translate('All Projects'),
          folder_name_en: this.translateService.translate('All Projects'),
          id: '0',
          parent_id: null,
          count_of_projects: null,
          sub_folders: null,
        },
        ...sortedFolders,
      ];
    } else {
      return sortedFolders;
    }
  }

  private prepareFlatFolders(folders: Folder[]) {
    const flatFolders = [];
    const lang = this.translateService.getActiveLang();
    const isLtr = lang === 'en' || lang === 'ru';
    const arrowIcon = isLtr ? '&#9205; ' : '&#9204; ';
    const insideIcon = isLtr ? '&#9495;&#9473;' : '&#9497;&#9473;';
    folders.forEach((e) => {
      flatFolders.push({
        folder_name: arrowIcon + e.folder_name,
        folder_name_en: arrowIcon + e.folder_name_en,
        id: e.id,
        folder_path: e.folder_name_en,
      });
      if (e.sub_folders) {
        e.sub_folders.forEach((l) => {
          flatFolders.push({
            folder_name: insideIcon + '&#160;&#160;  ' + l.folder_name,
            folder_name_en: '&#160;&#160;&#160;' + insideIcon + arrowIcon + l.folder_name_en,
            folder_path: e.folder_name_en + ' / ' + l.folder_name_en,
            id: l.id,
          });
        });
      }
    });

    return flatFolders;
  }

  //#region --old APIs
  private api_createNewFolder(body: any): Observable<any> {
    return this.endPoint.post('create_new_folder', body);
  }

  private api_deleteFolder(body: { flag: any; folder_id: any }): Observable<any> {
    return this.endPoint.post('delete', body, null, 'folders');
  }

  private api_moveToFolder(body: {
    project_ids: string[] | '*';
    label_ids: string[];
    excluded_label_ids: string[];
    exclude_ids: string[];
  }): Observable<{ success: boolean; status: string }> {
    return this.endPoint.post('move-to-labels', body, null, 'projects');
  }

  private api_updateFolder(body: any): Observable<any> {
    return this.endPoint.post('update_folder', body);
  }

  //#endregion --old APIs

  //the new API for getting folders list
  private api_getListFoldersRefactored(queryParams): Observable<any> {
    return this.endPoint.get('folders', queryParams);
  }

  //new method to handel the folders get request
  private async getFoldersRefactored(
    perPage?: number,
    pageNo?: number,
  ): Promise<FoldersListResponse> {
    if (!this.per_page) {
      this.per_page = 20;
    }
    this._foldersParams.page = pageNo ?? this.page;
    this._foldersParams.per_page = perPage ?? this.per_page;
    this._foldersParams.name = this.searchText;
    return new Promise((resolve, reject) => {
      this.api_getListFoldersRefactored(this._foldersParams).subscribe(
        (foldersListRes) => {
          resolve(foldersListRes);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  async getFolders(perPage?: number, pageNo?: number, label_name?: string): Promise<void> {
    this.searchText = label_name;
    await this.getFoldersRefactored(perPage, pageNo)
      .then((response) => {
        this.folderMeta = response.meta_data;
        this.folderList = this.sortFolders(response.data, true);
      })
      .catch((error) => {
        this._toaster.error(error.error?.message);
      });
  }
}
